import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useSnackbar } from 'notistack';
import ColorPickerField from '../../components/ColorPickerField';
import { brandConfig } from '../../branding/brandConfig';
import useLicenseSelect from '../../hooks/selects/useLicenseSelect';
import RegionAutocomplete from '../../components/saas/RegionAutocomplete';
import TimeZoneAutocomplete from '../../components/saas/TimeZoneAutocomplete';
import LanguageSelect from '../../components/saas/LanguageSelect';
import PaymentOccurrenceSelect from '../../components/saas/PaymentOccurrenceSelect';
import { DEFAULT_IANA_TIME_ZONE } from '../../utils/timeZoneOptions';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import mobileApplicationReleaseProfileService from '../../services/mobileApplicationReleaseProfileService';
import roleCatalogService from '../roleCatalog/roleCatalogService';
import BuildApiKeyRevealDialog from '../mobileApplicationReleaseProfiles/BuildApiKeyRevealDialog';
import tenantGroupService from '../tenantGroups/tenantGroupService';
import { TenantTypes } from './TenantForm';
import tenantOnboardingService, {
  TenantOnboardingDraft,
  TenantOnboardingPayload,
} from './tenantOnboardingService';
import {
  isRasterImageFile,
  prepareImageForUpload,
} from '../../lib/prepareImageForUpload';

const STEP_LABELS = [
  'Alapadatok',
  'Csomag',
  'Branding',
  'Publisher + modulok',
  'Meghívás',
  'Release profil + Firebase',
  'CI preview',
  'Összegzés',
];

const parseModuleCodes = (value: string | string[] | undefined | null) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return value
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
};

const DEPLOYMENT_MODE = {
  singleTenant: 0,
  aggregator: 1,
} as const;

const slugify = (value: string) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const defaultPayload = (): TenantOnboardingPayload => ({
  tenant: {
    name: '',
    slug: '',
    type: TenantTypes.Organization,
    contactName: '',
    contactEmail: '',
    region: '',
    defaultLanguage: 'hu',
    timeZone: 'Europe/Budapest',
    tenantGroupId: null,
    groupDisplayName: '',
    deepLinkSlug: '',
  },
  subscription: {
    paymentOccurrence: 'Monthly',
    startsAt: new Date().toISOString().substring(0, 10),
  },
  branding: {
    productName: brandConfig.productName,
    loginTitle: brandConfig.loginWelcome,
    primaryColor: brandConfig.colors.primary,
    secondaryColor: brandConfig.colors.secondary,
  },
  publisher: {
    name: '',
    enabledModules: [],
  },
  invitations: [],
  releaseProfile: {
    name: '',
    appTitle: '',
    enableParking: false,
    enableHtmlApplets: true,
    enableAnalytics: true,
    deploymentMode: DEPLOYMENT_MODE.singleTenant,
    tenantGroupId: null,
    existingProfileId: null,
  },
});

const BUILD_FILE_KIND = {
  googleServicesJson: 0,
  googleServiceInfoPlist: 1,
} as const;

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

const TenantOnboardingWizardPage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const { LicenseSelect, licenses } = useLicenseSelect();
  const multiTenancyEnabled = !!useSelector(
    (state: any) => state.app.featureFlags?.multiTenancyEnabled
  );

  const draftIdFromRoute = params.draftId;
  const draftIdFromQuery = searchParams.get('draftId');
  const initialDraftId = draftIdFromRoute || draftIdFromQuery || null;

  const [activeStep, setActiveStep] = useState(0);
  const [draft, setDraft] = useState<TenantOnboardingDraft | null>(null);
  const [payload, setPayload] = useState<TenantOnboardingPayload>(defaultPayload());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleTemplates, setRoleTemplates] = useState<any[]>([]);
  const [tenantGroups, setTenantGroups] = useState<any[]>([]);
  const [aggregatorProfiles, setAggregatorProfiles] = useState<any[]>([]);
  const [aggregatorProfileMode, setAggregatorProfileMode] = useState<'new' | 'existing'>(
    'new'
  );
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState<string | null>(null);
  const [revealDialog, setRevealDialog] = useState<{
    open: boolean;
    key: string;
    title: string;
    next?: () => void;
  }>({ open: false, key: '', title: '' });
  const loadedDraftIdRef = useRef<string | null>(null);
  const createLockRef = useRef(false);

  const licenseModuleOptions = useMemo(() => {
    const licenseId = payload.subscription?.licenseId;
    if (!licenseId) return [];
    const license = licenses.find((x) => x.id === licenseId);
    return parseModuleCodes(license?.includedModuleCodes);
  }, [licenses, payload.subscription?.licenseId]);

  const syncEnabledModulesToLicense = useCallback(
    (licenseId: string | null, prevModules?: string[]) => {
      if (!licenseId) return [];
      const license = licenses.find((x) => x.id === licenseId);
      const allowed = parseModuleCodes(license?.includedModuleCodes);
      if (!allowed.length) return [];
      const current = prevModules ?? [];
      const kept = current.filter((m) => allowed.includes(m));
      // First pick / empty selection: default to full license package.
      return kept.length > 0 ? kept : allowed;
    },
    [licenses]
  );

  const mergePayload = useCallback((base: TenantOnboardingPayload, incoming?: TenantOnboardingPayload) => {
    const defaults = defaultPayload();
    return {
      tenant: { ...defaults.tenant, ...base.tenant, ...incoming?.tenant },
      subscription: { ...defaults.subscription, ...base.subscription, ...incoming?.subscription },
      branding: { ...defaults.branding, ...base.branding, ...incoming?.branding },
      publisher: { ...defaults.publisher, ...base.publisher, ...incoming?.publisher },
      invitations: incoming?.invitations ?? base.invitations ?? [],
      releaseProfile: { ...defaults.releaseProfile, ...base.releaseProfile, ...incoming?.releaseProfile },
    };
  }, []);

  useEffect(() => {
    roleCatalogService.listTemplates().then((response) => {
      if (!response.hasError) setRoleTemplates(response.records ?? []);
    });
  }, []);

  useEffect(() => {
    if (!multiTenancyEnabled) return;
    tenantGroupService.list().then((response) => {
      if (!response.hasError) setTenantGroups(response.records ?? []);
    });
    mobileApplicationReleaseProfileService.list().then((response) => {
      if (response.hasError) return;
      const rows = response.records ?? response.result ?? response ?? [];
      const list = Array.isArray(rows) ? rows : [];
      setAggregatorProfiles(
        list.filter((p: any) => {
          const mode = Number(p.deploymentMode ?? p.DeploymentMode ?? 0);
          return mode === DEPLOYMENT_MODE.aggregator && !p.isDeleted;
        })
      );
    });
  }, [multiTenancyEnabled]);

  // Drop selected modules that fall outside the current license (e.g. after draft load).
  useEffect(() => {
    const licenseId = payload.subscription?.licenseId;
    if (!licenseId || licenses.length === 0) return;
    const allowed = parseModuleCodes(
      licenses.find((x) => x.id === licenseId)?.includedModuleCodes
    );
    setPayload((prev) => {
      const current = prev.publisher?.enabledModules ?? [];
      const pruned = current.filter((m) => allowed.includes(m));
      if (pruned.length === current.length) return prev;
      return {
        ...prev,
        publisher: { ...prev.publisher, enabledModules: pruned },
      };
    });
  }, [licenses, payload.subscription?.licenseId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (initialDraftId && loadedDraftIdRef.current === initialDraftId) {
        setLoading(false);
        return;
      }

      dispatch({ type: 'SHOW_QUERY' });
      setLoading(true);
      try {
        if (initialDraftId) {
          const response = await tenantOnboardingService.get(initialDraftId);
          if (cancelled) return;
          if (response.hasError) {
            enqueueSnackbar((response.errorMessages ?? []).join(','), { variant: 'error' });
            navigate('/tenants');
            return;
          }
          const loaded = response.result as TenantOnboardingDraft;
          loadedDraftIdRef.current = loaded.id;
          setDraft(loaded);
          const merged = mergePayload(defaultPayload(), loaded.payload);
          const blob =
            loaded.tempLogoBlobUrl ||
            merged.branding?.tempLogoBlobUrl ||
            merged.branding?.logoUrl ||
            null;
          setPayload({
            ...merged,
            branding: {
              ...merged.branding,
              tempLogoBlobUrl: blob,
              logoUrl: blob,
              logoPreviewUrl:
                loaded.logoPreviewUrl ||
                merged.branding?.logoPreviewUrl ||
                null,
            },
          });
          setActiveStep(Math.max(0, (loaded.currentStep ?? 1) - 1));
        } else {
          // Ne hozzunk létre draftot mountkor (Strict Mode dupla create elkerülése).
          // Az első Mentés / Következő hozza létre.
          loadedDraftIdRef.current = null;
          setDraft(null);
          setPayload(defaultPayload());
          setActiveStep(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          dispatch({ type: 'HIDE' });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [initialDraftId]);

  const saveDraft = async (
    stepIndex: number,
    exitAfter = false,
    options?: { manageLoading?: boolean; silent?: boolean }
  ): Promise<string | null> => {
    const manageLoading = options?.manageLoading !== false;
    if (manageLoading) {
      setSaving(true);
      dispatch({ type: 'SHOW_SAVE' });
    }
    try {
      let current = draft;
      if (!current?.id) {
        if (createLockRef.current) {
          return null;
        }
        createLockRef.current = true;
        try {
          const created = await tenantOnboardingService.create({
            currentStep: stepIndex + 1,
            payload,
          });
          if (created.hasError) {
            enqueueSnackbar((created.errorMessages ?? []).join(','), { variant: 'error' });
            return null;
          }
          current = created.result as TenantOnboardingDraft;
          loadedDraftIdRef.current = current.id;
          setDraft(current);
          navigate(`/tenants/new/${current.id}`, { replace: true });
        } finally {
          createLockRef.current = false;
        }
      }

      const response = await tenantOnboardingService.update(current.id, {
        currentStep: stepIndex + 1,
        payload,
        tempLogoBlobUrl: payload.branding?.tempLogoBlobUrl ?? current.tempLogoBlobUrl,
      });
      if (response.hasError) {
        enqueueSnackbar((response.errorMessages ?? []).join(','), { variant: 'error' });
        return null;
      }
      const updated = response.result as TenantOnboardingDraft;
      setDraft(updated);
      if (updated.logoPreviewUrl || updated.tempLogoBlobUrl) {
        setPayload((prev) => ({
          ...prev,
          branding: {
            ...prev.branding,
            tempLogoBlobUrl:
              updated.tempLogoBlobUrl ?? prev.branding?.tempLogoBlobUrl,
            logoUrl: updated.tempLogoBlobUrl ?? prev.branding?.logoUrl,
            logoPreviewUrl:
              updated.logoPreviewUrl ?? prev.branding?.logoPreviewUrl,
          },
        }));
      }
      if (!options?.silent) {
        enqueueSnackbar('Mentve', { variant: 'success' });
      }
      if (exitAfter) {
        navigate('/tenants');
      }
      return updated.id;
    } finally {
      if (manageLoading) {
        setSaving(false);
        dispatch({ type: 'HIDE' });
      }
    }
  };

  const handleNext = async () => {
    const draftId = await saveDraft(activeStep);
    if (!draftId) return;
    if (activeStep < STEP_LABELS.length - 1) {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleSaveAndExit = async () => {
    await saveDraft(activeStep, true);
  };

  const showRevealSequence = (keys: { mobileApiKey?: string; buildApiKey?: string; tenantId?: string }) => {
    const showMobile = () => {
      if (keys.mobileApiKey) {
        setRevealDialog({
          open: true,
          key: keys.mobileApiKey,
          title: 'Mobil API kulcs — másold el most',
          next: keys.buildApiKey ? showBuild : finishNavigate,
        });
      } else if (keys.buildApiKey) {
        showBuild();
      } else {
        finishNavigate();
      }
    };
    const showBuild = () => {
      setRevealDialog({
        open: true,
        key: keys.buildApiKey!,
        title: 'Build API kulcs — másold el most',
        next: finishNavigate,
      });
    };
    const finishNavigate = () => {
      if (keys.tenantId) {
        navigate(`/tenants/${keys.tenantId}/overview`);
      } else {
        navigate('/tenants');
      }
    };
    showMobile();
  };

  const handleComplete = async () => {
    setSaving(true);
    dispatch({ type: 'SHOW', payload: 'Bérlő létrehozása folyamatban...' });
    try {
      // Draft mentés közben ne zárja le a wait layert — a complete hívás hosszabb.
      const draftId = await saveDraft(activeStep, false, { manageLoading: false, silent: true });
      if (!draftId) return;
      const response = await tenantOnboardingService.complete(draftId);
      if (response.hasError) {
        enqueueSnackbar((response.errorMessages ?? []).join(','), { variant: 'error' });
        return;
      }
      const result = response.result ?? {};
      const tenantId =
        result.tenantId ??
        result.TenantId ??
        result.completedTenantId ??
        result.CompletedTenantId;
      const mobileApiKey = result.mobileApiKey ?? result.MobileApiKey ?? result.apiKey ?? result.ApiKey;
      const buildApiKey = result.buildApiKey ?? result.BuildApiKey ?? result.plainTextBuildApiKey;
      showRevealSequence({ mobileApiKey, buildApiKey, tenantId });
    } finally {
      setSaving(false);
      dispatch({ type: 'HIDE' });
    }
  };

  const uploadFirebaseFile = async (file: File, kind: number, fieldId: string, fieldObj: string) => {
    const contentBase64 = await fileToBase64(file);
    const response = await mobileApplicationReleaseProfileService.uploadBuildConfigFile({
      kind,
      fileName: file.name,
      contentBase64,
    });
    if (response.hasError) {
      enqueueSnackbar((response.errorMessages ?? []).join(','), { variant: 'error' });
      return;
    }
    setPayload((prev) => ({
      ...prev,
      releaseProfile: {
        ...prev.releaseProfile,
        [fieldId]: response.result.id,
        [fieldObj]: response.result,
      },
    }));
    enqueueSnackbar('Firebase fájl feltöltve.', { variant: 'success' });
  };

  const uploadTempLogo = async (file: File) => {
    // Draft kell a blob feltöltéshez — hozd létre, ha még nincs.
    let draftId = draft?.id ?? loadedDraftIdRef.current;
    if (!draftId) {
      draftId = await saveDraft(activeStep, false, {
        manageLoading: false,
        silent: true,
      });
      if (!draftId) {
        enqueueSnackbar('Draft létrehozása sikertelen a logó feltöltéséhez.', {
          variant: 'error',
        });
        return;
      }
    }

    try {
      dispatch({ type: 'SHOW_SAVE' });
      let uploadFile = file;
      if (isRasterImageFile(file)) {
        try {
          uploadFile = await prepareImageForUpload(file, {
            fileName: file.name,
          });
        } catch {
          enqueueSnackbar(
            'A kép nem készíthető elő feltöltésre. Próbálj kisebb fájlt vagy más formátumot.',
            { variant: 'error' }
          );
          return;
        }
      }

      const response = await tenantOnboardingService.uploadTempLogo(draftId, {
        fileName: uploadFile.name,
        contentBase64: await fileToBase64(uploadFile),
      });
      if (response.hasError) {
        enqueueSnackbar(
          (response.errorMessages ?? []).join(',') || 'Logó feltöltés sikertelen.',
          { variant: 'error' }
        );
        return;
      }

      const blobUrl =
        response.result?.tempLogoBlobUrl ??
        response.result?.TempLogoBlobUrl ??
        null;
      if (!blobUrl) {
        enqueueSnackbar('A logó URL hiányzik a válaszból.', { variant: 'error' });
        return;
      }

      const previewUrl =
        response.result?.logoPreviewUrl ??
        response.result?.LogoPreviewUrl ??
        URL.createObjectURL(uploadFile);

      setPayload((prev) => ({
        ...prev,
        branding: {
          ...prev.branding,
          tempLogoBlobUrl: blobUrl,
          logoUrl: blobUrl,
          logoPreviewUrl: previewUrl,
        },
      }));
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              tempLogoBlobUrl: blobUrl,
              logoPreviewUrl: previewUrl,
            }
          : prev
      );
      enqueueSnackbar('Logó feltöltve.', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Logó feltöltés sikertelen.', {
        variant: 'error',
      });
    } finally {
      dispatch({ type: 'HIDE' });
    }
  };

  const reusingExistingAggregatorProfile =
    multiTenancyEnabled &&
    aggregatorProfileMode === 'existing' &&
    !!payload.releaseProfile?.existingProfileId;

  const summaryItems = useMemo(
    () => [
      { label: 'Bérlő név', value: payload.tenant?.name },
      { label: 'Slug', value: payload.tenant?.slug || '(auto)' },
      ...(multiTenancyEnabled
        ? [
            {
              label: 'Bérlőcsoport',
              value:
                tenantGroups.find((g) => g.id === payload.tenant?.tenantGroupId)?.name ||
                payload.tenant?.tenantGroupId,
            },
            {
              label: 'Megjelenített név',
              value: payload.tenant?.groupDisplayName || payload.tenant?.name,
            },
            {
              label: 'Deep link slug',
              value:
                payload.tenant?.deepLinkSlug ||
                slugify(payload.tenant?.groupDisplayName || payload.tenant?.name || ''),
            },
          ]
        : []),
      { label: 'Licenc', value: payload.subscription?.licenseId },
      { label: 'Terméknév', value: payload.branding?.productName },
      { label: 'Publisher', value: payload.publisher?.name },
      { label: 'Meghívások', value: (payload.invitations?.length ?? 0).toString() },
      { label: 'Release profil', value: payload.releaseProfile?.name },
      ...(multiTenancyEnabled
        ? [
            {
              label: 'Kitelepítési mód',
              value:
                payload.releaseProfile?.deploymentMode === DEPLOYMENT_MODE.aggregator
                  ? 'Aggregátor'
                  : 'Single (egy bérlő)',
            },
          ]
        : []),
    ],
    [payload, multiTenancyEnabled, tenantGroups]
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Betöltés…</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant="h5">Új bérlő</Typography>
          {draft?.id && (
            <Typography variant="body2" color="text.secondary">
              Draft: {draft.id}
            </Typography>
          )}
        </Grid>

        <Grid size={12}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEP_LABELS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>

        <Grid size={12}>
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Alapadatok</Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label="Bérlő név"
                    value={payload.tenant?.name ?? ''}
                    onChange={(e) => {
                      const name = e.target.value;
                      setPayload((prev) => {
                        const prevDisplay = prev.tenant?.groupDisplayName ?? '';
                        const prevName = prev.tenant?.name ?? '';
                        const shouldSyncDisplay =
                          multiTenancyEnabled &&
                          (!prevDisplay || prevDisplay === prevName);
                        const nextDisplay = shouldSyncDisplay ? name : prevDisplay;
                        const prevDeep = prev.tenant?.deepLinkSlug ?? '';
                        const shouldSyncDeep =
                          multiTenancyEnabled &&
                          (!prevDeep ||
                            prevDeep === slugify(prevDisplay || prevName));
                        return {
                          ...prev,
                          tenant: {
                            ...prev.tenant,
                            name,
                            ...(shouldSyncDisplay
                              ? { groupDisplayName: nextDisplay }
                              : {}),
                            ...(shouldSyncDeep
                              ? { deepLinkSlug: slugify(nextDisplay || name) }
                              : {}),
                          },
                        };
                      });
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Slug"
                    helperText="Üresen hagyva automatikusan generálódik"
                    value={payload.tenant?.slug ?? ''}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, slug: e.target.value },
                      }))
                    }
                  />
                </Grid>
                {multiTenancyEnabled && (
                  <>
                    <Grid size={12}>
                      <Autocomplete
                        options={tenantGroups}
                        value={
                          tenantGroups.find(
                            (g) => g.id === payload.tenant?.tenantGroupId
                          ) ?? null
                        }
                        getOptionLabel={(option) => option?.name ?? ''}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        onChange={(_, value) =>
                          setPayload((prev) => ({
                            ...prev,
                            tenant: {
                              ...prev.tenant,
                              tenantGroupId: value?.id ?? null,
                            },
                            releaseProfile: {
                              ...prev.releaseProfile,
                              tenantGroupId:
                                prev.releaseProfile?.deploymentMode ===
                                DEPLOYMENT_MODE.aggregator
                                  ? value?.id ?? null
                                  : prev.releaseProfile?.tenantGroupId ?? null,
                            },
                          }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label="Bérlőcsoport"
                            helperText="Aggregátor városválasztóhoz kötelező"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        required
                        label="Megjelenített név (csoport)"
                        helperText="Picker / shortcut címke — alapértelmezés: bérlő név"
                        value={payload.tenant?.groupDisplayName ?? ''}
                        onChange={(e) => {
                          const groupDisplayName = e.target.value;
                          setPayload((prev) => {
                            const prevDeep = prev.tenant?.deepLinkSlug ?? '';
                            const shouldSyncDeep =
                              !prevDeep ||
                              prevDeep ===
                                slugify(
                                  prev.tenant?.groupDisplayName ||
                                    prev.tenant?.name ||
                                    ''
                                );
                            return {
                              ...prev,
                              tenant: {
                                ...prev.tenant,
                                groupDisplayName,
                                ...(shouldSyncDeep
                                  ? { deepLinkSlug: slugify(groupDisplayName) }
                                  : {}),
                              },
                            };
                          });
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        required
                        label="Deep link slug"
                        helperText="Csoporton belüli egyedi URL-safe slug"
                        value={payload.tenant?.deepLinkSlug ?? ''}
                        onChange={(e) =>
                          setPayload((prev) => ({
                            ...prev,
                            tenant: {
                              ...prev.tenant,
                              deepLinkSlug: slugify(e.target.value),
                            },
                          }))
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid size={12}>
                  <Autocomplete
                    options={[TenantTypes.Individual, TenantTypes.Organization, TenantTypes.Government]}
                    getOptionLabel={(v) =>
                      v === TenantTypes.Individual
                        ? 'Magánszemély'
                        : v === TenantTypes.Government
                          ? 'Kormányzati'
                          : 'Szervezet'
                    }
                    value={payload.tenant?.type ?? TenantTypes.Organization}
                    onChange={(_, v) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, type: v ?? TenantTypes.Organization },
                      }))
                    }
                    renderInput={(params) => <TextField {...params} label="Bérlő típus" />}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Kapcsolattartó neve"
                    value={payload.tenant?.contactName ?? ''}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, contactName: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Kapcsolattartó email"
                    value={payload.tenant?.contactEmail ?? ''}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, contactEmail: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <RegionAutocomplete
                    value={payload.tenant?.region ?? ''}
                    onChange={(code) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, region: code ?? '' },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <LanguageSelect
                    value={payload.tenant?.defaultLanguage ?? 'hu'}
                    onChange={(lang) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, defaultLanguage: lang },
                      }))
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <TimeZoneAutocomplete
                    value={
                      payload.tenant?.timeZone ?? DEFAULT_IANA_TIME_ZONE
                    }
                    onChange={(tz) =>
                      setPayload((prev) => ({
                        ...prev,
                        tenant: { ...prev.tenant, timeZone: tz },
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Csomag / előfizetés</Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <LicenseSelect
                    value={payload.subscription?.licenseId ?? null}
                    onChange={(_e: any, v: any) =>
                      setPayload((prev) => ({
                        ...prev,
                        subscription: { ...prev.subscription, licenseId: v },
                        publisher: {
                          ...prev.publisher,
                          enabledModules: syncEnabledModulesToLicense(
                            v,
                            prev.publisher?.enabledModules
                          ),
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PaymentOccurrenceSelect
                    value={payload.subscription?.paymentOccurrence ?? 'Monthly'}
                    onChange={(v) =>
                      setPayload((prev) => ({
                        ...prev,
                        subscription: {
                          ...prev.subscription,
                          paymentOccurrence: v,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <DatePicker
                    label="Kezdés dátuma"
                    value={
                      payload.subscription?.startsAt
                        ? dayjs(payload.subscription.startsAt)
                        : null
                    }
                    onChange={(d) =>
                      setPayload((prev) => ({
                        ...prev,
                        subscription: {
                          ...prev.subscription,
                          startsAt: d ? d.format('YYYY-MM-DD') : '',
                        },
                      }))
                    }
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Branding</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Terméknév"
                    value={payload.branding?.productName ?? ''}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, productName: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Belépő oldal szöveg"
                    value={payload.branding?.loginTitle ?? ''}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, loginTitle: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ColorPickerField
                    label="Elsődleges szín"
                    value={payload.branding?.primaryColor ?? ''}
                    defaultColor={brandConfig.colors.primary}
                    onChange={(hex) =>
                      setPayload((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, primaryColor: hex },
                      }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ColorPickerField
                    label="Másodlagos szín"
                    value={payload.branding?.secondaryColor ?? ''}
                    defaultColor={brandConfig.colors.secondary}
                    onChange={(hex) =>
                      setPayload((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, secondaryColor: hex },
                      }))
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Logó</Typography>
                  {(payload.branding?.logoPreviewUrl ||
                    payload.branding?.tempLogoBlobUrl) && (
                    <Box sx={{ mb: 1 }}>
                      <Box
                        component="img"
                        src={
                          payload.branding?.logoPreviewUrl ||
                          payload.branding?.tempLogoBlobUrl ||
                          undefined
                        }
                        alt="Logo preview"
                        sx={{
                          display: 'block',
                          maxHeight: 80,
                          maxWidth: 240,
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  )}
                  <Button variant="outlined" component="label">
                    Logó feltöltése
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      hidden
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await uploadTempLogo(file);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Publisher és modulok</Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label="Alapértelmezett publisher neve"
                    value={payload.publisher?.name ?? ''}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        publisher: { ...prev.publisher, name: e.target.value },
                      }))
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <Autocomplete
                    multiple
                    disabled={!payload.subscription?.licenseId}
                    options={licenseModuleOptions}
                    value={(payload.publisher?.enabledModules ?? []).filter((m) =>
                      licenseModuleOptions.includes(m)
                    )}
                    onChange={(_, v) =>
                      setPayload((prev) => ({
                        ...prev,
                        publisher: { ...prev.publisher, enabledModules: v },
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Üzemi modulok"
                        helperText={
                          !payload.subscription?.licenseId
                            ? 'Előbb válassz licencet a Csomag lépésben.'
                            : licenseModuleOptions.length === 0
                              ? 'A kiválasztott licenchez nincs modul hozzárendelve.'
                              : 'Csak a licencben foglalt modulok. A kereskedelmi jogosultságot a csomag adja; itt üzemi szűkítést állíthatsz.'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 4 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Meghívások</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Autocomplete
                    options={roleTemplates}
                    getOptionLabel={(o) => o.name ?? o.Name ?? ''}
                    value={
                      roleTemplates.find(
                        (t) => (t.id ?? t.Id) === inviteRoleId
                      ) ?? null
                    }
                    onChange={(_, v) => setInviteRoleId(v ? (v.id ?? v.Id) : null)}
                    renderInput={(params) => <TextField {...params} label="Szerepkör sablon" />}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      if (!inviteEmail.trim()) return;
                      setPayload((prev) => ({
                        ...prev,
                        invitations: [
                          ...(prev.invitations ?? []),
                          { email: inviteEmail.trim(), roleTemplateId: inviteRoleId },
                        ],
                      }));
                      setInviteEmail('');
                      setInviteRoleId(null);
                    }}
                  >
                    Hozzáadás
                  </Button>
                </Grid>
                <Grid size={12}>
                  {(payload.invitations ?? []).map((inv, idx) => (
                    <Chip
                      key={`${inv.email}-${idx}`}
                      label={`${inv.email}${inv.roleTemplateId ? ` (${inv.roleTemplateId})` : ''}`}
                      onDelete={() =>
                        setPayload((prev) => ({
                          ...prev,
                          invitations: (prev.invitations ?? []).filter((_, i) => i !== idx),
                        }))
                      }
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                  {(payload.invitations?.length ?? 0) === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Opcionális — később is meghívhatod a bérlő admin felületén.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 5 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Release profil + Firebase</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                A beállítások új build után lépnek életbe (Play Store / TestFlight).
              </Alert>
              <Grid container spacing={2}>
                {multiTenancyEnabled && (
                  <Grid size={12}>
                    <TextField
                      select
                      fullWidth
                      label="Release profil"
                      value={aggregatorProfileMode}
                      onChange={(e) => {
                        const mode = e.target.value as 'new' | 'existing';
                        setAggregatorProfileMode(mode);
                        setPayload((prev) => ({
                          ...prev,
                          releaseProfile: {
                            ...prev.releaseProfile,
                            existingProfileId: null,
                            deploymentMode:
                              mode === 'existing'
                                ? DEPLOYMENT_MODE.aggregator
                                : prev.releaseProfile?.deploymentMode ??
                                  DEPLOYMENT_MODE.singleTenant,
                            tenantGroupId:
                              mode === 'existing'
                                ? null
                                : prev.releaseProfile?.tenantGroupId ?? null,
                          },
                        }));
                      }}
                      helperText="Single mindig új profil. Aggregátor app: meglévő profil több bérlőhöz is használható."
                    >
                      <MenuItem value="existing">
                        Meglévő aggregátor profil
                      </MenuItem>
                      <MenuItem value="new">Új release profil létrehozása</MenuItem>
                    </TextField>
                  </Grid>
                )}

                {multiTenancyEnabled && aggregatorProfileMode === 'existing' && (
                  <Grid size={12}>
                    <Autocomplete
                      options={aggregatorProfiles}
                      value={
                        aggregatorProfiles.find(
                          (p) => p.id === payload.releaseProfile?.existingProfileId
                        ) ?? null
                      }
                      getOptionLabel={(option) => {
                        const groupName =
                          tenantGroups.find(
                            (g) =>
                              g.id ===
                              (option.tenantGroupId ?? option.TenantGroupId)
                          )?.name ?? '';
                        return groupName
                          ? `${option.name} — ${groupName}`
                          : option.name ?? '';
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onChange={(_, value) => {
                        const groupId =
                          value?.tenantGroupId ?? value?.TenantGroupId ?? null;
                        setPayload((prev) => ({
                          ...prev,
                          releaseProfile: {
                            ...prev.releaseProfile,
                            existingProfileId: value?.id ?? null,
                            deploymentMode: DEPLOYMENT_MODE.aggregator,
                            tenantGroupId: groupId,
                            name: value?.name ?? prev.releaseProfile?.name,
                          },
                          tenant: {
                            ...prev.tenant,
                            // Profile defines the aggregator group membership.
                            tenantGroupId: groupId ?? prev.tenant?.tenantGroupId,
                          },
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Aggregátor profil"
                          helperText="Csak Aggregátor profilok. A kitelepítés és a bérlőcsoport a profilból jön."
                        />
                      )}
                    />
                  </Grid>
                )}

                {(!multiTenancyEnabled || aggregatorProfileMode === 'new') && (
                  <>
                    {multiTenancyEnabled && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="Kitelepítési mód"
                          value={
                            payload.releaseProfile?.deploymentMode ??
                            DEPLOYMENT_MODE.singleTenant
                          }
                          onChange={(e) => {
                            const nextMode = Number(e.target.value);
                            setPayload((prev) => ({
                              ...prev,
                              releaseProfile: {
                                ...prev.releaseProfile,
                                deploymentMode: nextMode,
                                existingProfileId: null,
                                tenantGroupId:
                                  nextMode === DEPLOYMENT_MODE.aggregator
                                    ? prev.tenant?.tenantGroupId ??
                                      prev.releaseProfile?.tenantGroupId ??
                                      null
                                    : null,
                              },
                            }));
                          }}
                          helperText="Single: egy bérlő → mindig új profil. Aggregátor: új app a csoporthoz."
                        >
                          <MenuItem value={DEPLOYMENT_MODE.singleTenant}>
                            Single (egy bérlő)
                          </MenuItem>
                          <MenuItem value={DEPLOYMENT_MODE.aggregator}>
                            Aggregátor
                          </MenuItem>
                        </TextField>
                      </Grid>
                    )}
                    {multiTenancyEnabled &&
                      payload.releaseProfile?.deploymentMode ===
                        DEPLOYMENT_MODE.aggregator && (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Autocomplete
                            options={tenantGroups}
                            value={
                              tenantGroups.find(
                                (g) =>
                                  g.id ===
                                  (payload.releaseProfile?.tenantGroupId ??
                                    payload.tenant?.tenantGroupId)
                              ) ?? null
                            }
                            getOptionLabel={(option) => option?.name ?? ''}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            onChange={(_, value) =>
                              setPayload((prev) => ({
                                ...prev,
                                releaseProfile: {
                                  ...prev.releaseProfile,
                                  tenantGroupId: value?.id ?? null,
                                  existingProfileId: null,
                                },
                                tenant: {
                                  ...prev.tenant,
                                  tenantGroupId:
                                    prev.tenant?.tenantGroupId ??
                                    value?.id ??
                                    null,
                                },
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label="Bérlőcsoport"
                                helperText="Az új aggregátor app melyik csoport városait listázza."
                              />
                            )}
                          />
                        </Grid>
                      )}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        label="Profil neve"
                        value={payload.releaseProfile?.name ?? ''}
                        onChange={(e) =>
                          setPayload((prev) => ({
                            ...prev,
                            releaseProfile: {
                              ...prev.releaseProfile,
                              name: e.target.value,
                            },
                          }))
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="App cím"
                        value={payload.releaseProfile?.appTitle ?? ''}
                        onChange={(e) =>
                          setPayload((prev) => ({
                            ...prev,
                            releaseProfile: {
                              ...prev.releaseProfile,
                              appTitle: e.target.value,
                            },
                          }))
                        }
                      />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!payload.releaseProfile?.enableParking}
                            onChange={(e) =>
                              setPayload((prev) => ({
                                ...prev,
                                releaseProfile: {
                                  ...prev.releaseProfile,
                                  enableParking: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label="Parkolás engedélyezése"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              payload.releaseProfile?.enableHtmlApplets !== false
                            }
                            onChange={(e) =>
                              setPayload((prev) => ({
                                ...prev,
                                releaseProfile: {
                                  ...prev.releaseProfile,
                                  enableHtmlApplets: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label="HTML appletok"
                      />
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="subtitle2">
                        google-services.json
                      </Typography>
                      {payload.releaseProfile?.googleServicesJsonFile?.fileName && (
                        <Typography variant="body2">
                          Feltöltve:{' '}
                          {payload.releaseProfile.googleServicesJsonFile.fileName}
                        </Typography>
                      )}
                      <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                        Fájl feltöltése
                        <input
                          type="file"
                          accept=".json,application/json"
                          hidden
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              await uploadFirebaseFile(
                                file,
                                BUILD_FILE_KIND.googleServicesJson,
                                'googleServicesJsonFileId',
                                'googleServicesJsonFile'
                              );
                            }
                            e.target.value = '';
                          }}
                        />
                      </Button>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="subtitle2">
                        GoogleService-Info.plist
                      </Typography>
                      {payload.releaseProfile?.googleServiceInfoPlistFile
                        ?.fileName && (
                        <Typography variant="body2">
                          Feltöltve:{' '}
                          {
                            payload.releaseProfile.googleServiceInfoPlistFile
                              .fileName
                          }
                        </Typography>
                      )}
                      <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                        Fájl feltöltése
                        <input
                          type="file"
                          accept=".plist,application/xml,text/xml"
                          hidden
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              await uploadFirebaseFile(
                                file,
                                BUILD_FILE_KIND.googleServiceInfoPlist,
                                'googleServiceInfoPlistFileId',
                                'googleServiceInfoPlistFile'
                              );
                            }
                            e.target.value = '';
                          }}
                        />
                      </Button>
                    </Grid>
                  </>
                )}

                {multiTenancyEnabled && aggregatorProfileMode === 'existing' && (
                  <Grid size={12}>
                    <Alert severity="info">
                      A kiválasztott Aggregátor profil határozza meg a bérlőcsoportot.
                      Nincs új Firebase / build kulcs — a bérlő a profil csoportjának tagja
                      lesz.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {activeStep === 6 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>CI preview</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                {reusingExistingAggregatorProfile ? (
                  <>
                    Meglévő aggregátor profil: a pipeline <code>CMS_BUILD_PROFILE_ID</code> és
                    build API kulcs változatlan — már be van állítva. Új bérlőnél csak a{' '}
                    <strong>mobil API kulcs</strong> jelenik meg.
                  </>
                ) : (
                  <>
                    A bérlő létrehozása után a release profil <strong>PublicId (GUID)</strong> lesz
                    a pipeline <code>CMS_BUILD_PROFILE_ID</code> változó értéke. A build API kulcsot
                    a befejezéskor egyszer megjelenítjük.
                  </>
                )}
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Várható pipeline beállítások</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Profil név: {payload.releaseProfile?.name || '—'}
                </Typography>
                <Typography variant="body2">
                  App cím: {payload.releaseProfile?.appTitle || payload.tenant?.name || '—'}
                </Typography>
                {multiTenancyEnabled && (
                  <Typography variant="body2">
                    Kitelepítési mód:{' '}
                    {payload.releaseProfile?.deploymentMode === DEPLOYMENT_MODE.aggregator
                      ? 'Aggregátor (TENANT_GROUP_ID)'
                      : 'Single (egy bérlő)'}
                  </Typography>
                )}
                <Typography variant="body2">
                  Firebase Android:{' '}
                  {payload.releaseProfile?.googleServicesJsonFile?.fileName ? '✓' : '—'}
                </Typography>
                <Typography variant="body2">
                  Firebase iOS:{' '}
                  {payload.releaseProfile?.googleServiceInfoPlistFile?.fileName ? '✓' : '—'}
                </Typography>
              </Paper>
            </Box>
          )}

          {activeStep === 7 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Összegzés</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {summaryItems.map((item) => (
                  <Typography key={item.label} variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{item.label}:</strong> {item.value || '—'}
                  </Typography>
                ))}
              </Paper>
              <Alert severity="warning" sx={{ mt: 2 }}>
                {reusingExistingAggregatorProfile
                  ? 'A befejezés után csak a mobil API kulcs jelenik meg egyszer — másold le azonnal. Build API kulcs nem készül (meglévő aggregátor profil).'
                  : 'A befejezés után a mobil és build API kulcsok egyszer megjelennek — másold le azonnal.'}
              </Alert>
            </Box>
          )}
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button disabled={activeStep === 0 || saving} onClick={handleBack}>
              Vissza
            </Button>
            <Button variant="outlined" disabled={saving} onClick={handleSaveAndExit}>
              Mentés és kilépés
            </Button>
            {activeStep < STEP_LABELS.length - 1 ? (
              <Button variant="contained" disabled={saving} onClick={handleNext}>
                Következő
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={saving}
                onClick={handleComplete}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saving ? 'Létrehozás...' : 'Befejezés és bérlő létrehozása'}
              </Button>
            )}
            <Button variant="text" onClick={() => navigate('/tenants')}>
              Mégse
            </Button>
          </Box>
        </Grid>
      </Grid>

      <BuildApiKeyRevealDialog
        open={revealDialog.open}
        apiKey={revealDialog.key}
        title={revealDialog.title}
        onClose={() => {
          setRevealDialog((prev) => ({ ...prev, open: false }));
          if (revealDialog.next) {
            revealDialog.next();
          }
        }}
      />
    </Paper>
  );
};

export default TenantOnboardingWizardPage;
